<script setup>
import { ref, onMounted, onBeforeUnmount, computed } from 'vue';
import { useRouter } from 'vue-router';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import { emitter } from 'shared/helpers/mitt';
import { BUS_EVENTS } from 'shared/constants/busEvents';
import TasksAPI from 'dashboard/api/tasks';

// Cola de alertas pendientes · si llegan varias seguidas se apilan y se muestran 1 a 1
const queue = ref([]);
const current = ref(null);
const router = useRouter();
const store = useStore();
const accountId = useMapGetter('getCurrentAccountId');

const audio = new Audio('/audio/dashboard/bell.mp3');
audio.volume = 0.6;

const onTaskAlert = data => {
  queue.value.push(data);
  if (!current.value) showNext();
};

const showNext = () => {
  if (!queue.value.length) {
    current.value = null;
    return;
  }
  current.value = queue.value.shift();
  try { audio.currentTime = 0; audio.play(); } catch (_) {}
};

const close = () => { current.value = null; setTimeout(showNext, 250); };

const goToTask = async () => {
  const t = task.value;
  // Marca la notification como leída (silencioso)
  if (current.value?.id) {
    try {
      await store.dispatch('notifications/read', {
        id: current.value.id,
        primaryActorType: current.value.primary_actor_type,
        primaryActorId: current.value.primary_actor_id,
        unreadCount: 0,
      });
    } catch (_) {}
  }
  // Navegar al chat asociado si existe; si no, ir al tab Tareas
  if (t?.conversation_id && t?.conversation?.display_id && accountId.value) {
    router.push(`/app/accounts/${accountId.value}/conversations/${t.conversation_id}`);
  } else if (accountId.value) {
    router.push(`/app/accounts/${accountId.value}/conversations`);
  }
  close();
};

const markSeen = async () => {
  if (current.value?.id) {
    try {
      await store.dispatch('notifications/read', {
        id: current.value.id,
        primaryActorType: current.value.primary_actor_type,
        primaryActorId: current.value.primary_actor_id,
        unreadCount: 0,
      });
    } catch (_) {}
  }
  close();
};

// === Datos del task que viene en primary_actor ===
const task = computed(() => current.value?.primary_actor || {});
const assignerName = computed(() => current.value?.secondary_actor?.name || 'Alguien');
const priority = computed(() => task.value?.priority || 'normal');
const isHighPriority = computed(
  () => priority.value === 'alta' || priority.value === 'urgente'
);
const priorityLabel = computed(() => {
  if (priority.value === 'urgente') return '⚠ Urgente';
  if (priority.value === 'alta') return '🔥 Alta';
  return 'Normal';
});
const dueLabel = computed(() => {
  const ts = task.value?.due_date;
  if (!ts) return null;
  const d = new Date(ts * 1000);
  const today = new Date(); today.setHours(0,0,0,0);
  const tomorrow = new Date(today); tomorrow.setDate(tomorrow.getDate() + 1);
  const dDay = new Date(d); dDay.setHours(0,0,0,0);
  const fmt = d.toLocaleTimeString('es-DO', { hour: 'numeric', minute: '2-digit', hour12: true });
  if (dDay.getTime() === today.getTime()) return `Hoy ${fmt}`;
  if (dDay.getTime() === tomorrow.getTime()) return `Mañana ${fmt}`;
  return d.toLocaleString('es-DO', { day: 'numeric', month: 'short', hour: 'numeric', minute: '2-digit', hour12: true });
});
const contactName = computed(() => task.value?.contact?.name || '—');
const channelLabel = computed(() => {
  const ch = task.value?.conversation?.channel_type || '';
  const map = {
    'Channel::Whatsapp': 'WhatsApp',
    'Channel::Api': 'Bridge',
    'Channel::FacebookPage': 'Facebook',
    'Channel::Instagram': 'Instagram',
  };
  return map[ch] || ch.replace('Channel::', '') || '—';
});

const onKeydown = e => {
  if (!current.value) return;
  if (e.key === 'Escape') markSeen();
  if (e.key === 'Enter') goToTask();
};

onMounted(() => {
  emitter.on(BUS_EVENTS.TASK_ASSIGNED_LIVE, onTaskAlert);
  document.addEventListener('keydown', onKeydown);
});
onBeforeUnmount(() => {
  emitter.off(BUS_EVENTS.TASK_ASSIGNED_LIVE, onTaskAlert);
  document.removeEventListener('keydown', onKeydown);
});
</script>

<template>
  <Teleport to="body">
    <div
      v-if="current"
      class="fixed inset-0 z-[10000] flex items-center justify-center bg-black/75 backdrop-blur-sm"
    >
      <div
        class="alert-modal w-[440px] max-w-[95vw] rounded-2xl overflow-hidden shadow-2xl"
        style="background: linear-gradient(180deg, rgba(34,30,22,.97) 0%, rgba(22,20,16,.99) 100%); border: 1px solid rgba(201,163,90,.35); animation: alert-rise .55s cubic-bezier(.34,1.56,.64,1);"
      >
        <!-- Header con campana grande pulsante -->
        <div class="p-6 text-center border-b" style="border-color: rgba(255,255,255,.06);">
          <div class="relative inline-block mb-3">
            <div
              class="w-16 h-16 rounded-full flex items-center justify-center"
              style="background: linear-gradient(135deg, #d4b87a 0%, #b8923f 100%); animation: alert-pulse 1.8s ease-in-out infinite;"
            >
              <svg class="w-8 h-8 text-amber-950 alert-wobble" fill="currentColor" viewBox="0 0 24 24">
                <path d="M12 22a2 2 0 0 0 2-2h-4a2 2 0 0 0 2 2zm6-6V11c0-3.07-1.64-5.64-4.5-6.32V4a1.5 1.5 0 0 0-3 0v.68C7.64 5.36 6 7.93 6 11v5l-2 2v1h16v-1l-2-2z"/>
              </svg>
            </div>
            <span
              class="absolute inset-0 rounded-full alert-ping"
              style="background: rgba(201,163,90,.5);"
            />
          </div>
          <div
            class="text-[10px] font-semibold tracking-widest"
            style="color:#c9a35a; animation: alert-glow 2s ease-in-out infinite;"
          >¡TIENES UNA NUEVA TAREA!</div>
          <div class="text-lg text-zinc-100 font-medium mt-2 px-2">{{ task.title }}</div>
          <div class="text-[11px] text-zinc-400 mt-1">te la asignó <span class="text-zinc-200 font-medium">{{ assignerName }}</span></div>
        </div>

        <!-- Detalles -->
        <div class="p-5 space-y-3 text-xs">
          <div v-if="task.conversation_id" class="flex items-center gap-2">
            <span class="text-zinc-500 w-20">Cliente</span>
            <span class="text-zinc-100 font-medium">{{ contactName }}</span>
          </div>
          <div v-if="task.conversation_id" class="flex items-center gap-2">
            <span class="text-zinc-500 w-20">Canal</span>
            <span class="text-zinc-200">{{ channelLabel }}</span>
          </div>
          <div class="flex items-center gap-2">
            <span class="text-zinc-500 w-20">Prioridad</span>
            <span
              :class="isHighPriority ? 'text-rose-400 font-semibold' : 'text-zinc-300'"
            >{{ priorityLabel }}</span>
          </div>
          <div v-if="dueLabel" class="flex items-center gap-2">
            <span class="text-zinc-500 w-20">Vence</span>
            <span class="text-amber-400 font-semibold">{{ dueLabel }}</span>
          </div>
          <div v-if="task.description" class="pt-2 border-t" style="border-color: rgba(255,255,255,.06);">
            <div class="text-[10px] text-zinc-500 tracking-wider mb-1">NOTAS</div>
            <div class="text-[11px] text-zinc-300 leading-relaxed">{{ task.description }}</div>
          </div>
        </div>

        <!-- Botones -->
        <div class="p-4 border-t flex gap-2 bg-black/20" style="border-color: rgba(255,255,255,.06);">
          <button
            class="flex-1 text-xs py-2.5 rounded text-zinc-300 border border-white/10 bg-white/5 hover:bg-white/10"
            @click="markSeen"
          >Marcar vista</button>
          <button
            class="flex-1 text-xs py-2.5 rounded font-semibold"
            style="background: linear-gradient(180deg, #d4b87a 0%, #b8923f 100%); color: #1a1610; border: 1px solid rgba(201,163,90,.4); box-shadow: 0 4px 12px rgba(201,163,90,.25);"
            @click="goToTask"
          >{{ task.conversation_id ? 'Ver y empezar →' : 'Ver tarea →' }}</button>
        </div>

        <!-- Indicador cola si hay más -->
        <div
          v-if="queue.length"
          class="px-4 py-1.5 text-[10px] text-zinc-500 text-center border-t"
          style="border-color: rgba(255,255,255,.06);"
        >+ {{ queue.length }} más en cola</div>
      </div>
    </div>
  </Teleport>
</template>

<style>
@keyframes alert-rise   { 0% { transform: translateY(40px) scale(.7); opacity:0; } 60% { transform: translateY(-8px) scale(1.05); } 100% { transform: translateY(0) scale(1); opacity:1; } }
@keyframes alert-pulse  { 0%,100% { box-shadow: 0 0 0 0 rgba(201,163,90,.7), 0 0 20px rgba(201,163,90,.4); } 50% { box-shadow: 0 0 0 24px rgba(201,163,90,0), 0 0 30px rgba(201,163,90,.6); } }
@keyframes alert-ping   { 0% { transform: scale(.8); opacity:1; } 100% { transform: scale(2.4); opacity:0; } }
@keyframes alert-glow   { 0%,100% { text-shadow: 0 0 4px rgba(201,163,90,.3); } 50% { text-shadow: 0 0 12px rgba(201,163,90,.7); } }
@keyframes alert-wobble { 0% { transform: rotate(0); } 5% { transform: rotate(-18deg); } 12% { transform: rotate(14deg); } 20% { transform: rotate(-10deg); } 28% { transform: rotate(7deg); } 35% { transform: rotate(-4deg); } 42%, 100% { transform: rotate(0); } }
.alert-wobble { animation: alert-wobble 3.5s ease-in-out infinite; transform-origin: top center; }
.alert-ping   { animation: alert-ping 1.6s ease-out infinite; }
</style>
